// =============================================================================
// src/app/context.ts — AppContext: container de Injecao de Dependencia minimo.
//
// Proveniencia: formaliza, num unico objeto, as dependencias que o mockup original
//   acessava por variaveis globais (DATA, mapController, sendPrompt). Cada
//   AppModule.init(ctx) recebe este container — nada de singleton/global escondido.
//
// O campo `map` nasce null: o MapController so existe apos o fetch assincrono dos
//   GeoJSON. A integracao (main.ts) recria o contexto, ou usa um setter, quando o
//   mapa montar. Mantemos o contrato readonly e expomos uma fabrica que troca `map`.
// =============================================================================

import type {
  AppBus,
  AppCommands,
  AppContext,
  AtalaiaClient,
  MapController,
  SectionInsightsMap,
  SentinelClient,
} from './types';
import { createAppBus } from './bus';
import { createAppCommands } from './commands';

/** Dependencias opcionais para sobrescrever em testes (DI por argumento). */
export interface AppContextOverrides {
  readonly bus?: AppBus;
  readonly commands?: AppCommands;
  readonly map?: MapController | null;
  readonly atalaia?: AtalaiaClient | null;
  readonly sentinel?: SentinelClient | null;
  readonly sectionInsights?: SectionInsightsMap | null;
}

/**
 * Cria um AppContext. Por padrao instancia bus e commands proprios e deixa `map` e
 * `atalaia` como null (preenchidos pelas fabricas quando o mapa monta / a integracao
 * ao vivo liga).
 *
 * @example
 *   const ctx = createAppContext();
 *   topbarModule.init(ctx);
 */
export function createAppContext(overrides: AppContextOverrides = {}): AppContext {
  return {
    bus: overrides.bus ?? createAppBus(),
    commands: overrides.commands ?? createAppCommands(),
    map: overrides.map ?? null,
    atalaia: overrides.atalaia ?? null,
    sentinel: overrides.sentinel ?? null,
    sectionInsights: overrides.sectionInsights ?? null,
  };
}

/**
 * Devolve uma copia do contexto com o MapController preenchido. Como AppContext e
 * readonly, a integracao usa esta fabrica ao inves de mutar o campo `map`.
 */
export function withMapController(
  context: AppContext,
  map: MapController,
): AppContext {
  return {
    bus: context.bus,
    commands: context.commands,
    map,
    atalaia: context.atalaia,
    sentinel: context.sentinel,
    sectionInsights: context.sectionInsights,
  };
}

/**
 * Devolve uma copia do contexto com o cliente AtalaIA preenchido (integracao ao vivo).
 * Espelha withMapController: mantem o contrato readonly em vez de mutar `atalaia`.
 */
export function withAtalaiaClient(
  context: AppContext,
  atalaia: AtalaiaClient,
): AppContext {
  return {
    bus: context.bus,
    commands: context.commands,
    map: context.map,
    atalaia,
    sentinel: context.sentinel,
    sectionInsights: context.sectionInsights,
  };
}

export function withSentinelClient(
  context: AppContext,
  sentinel: SentinelClient,
): AppContext {
  return {
    bus: context.bus,
    commands: context.commands,
    map: context.map,
    atalaia: context.atalaia,
    sentinel,
    sectionInsights: context.sectionInsights,
  };
}

export function withSectionInsights(
  context: AppContext,
  sectionInsights: SectionInsightsMap,
): AppContext {
  return {
    bus: context.bus,
    commands: context.commands,
    map: context.map,
    atalaia: context.atalaia,
    sentinel: context.sentinel,
    sectionInsights,
  };
}
